// V7.9 - useOfflineMode Hook
// Provides offline-aware functionality for QR Code validation and agent dispatch
// Does NOT alter any existing functionality — purely additive layer

import { useState, useEffect, useCallback } from 'react';
import {
  validateQRCodeOffline,
  getCachedAgents,
  saveOfflineEvent,
  saveOfflineImage,
  addToOutbox,
  generateIdempotencyKey,
  getOutboxCount,
  type OfflineAgent,
  type OfflineQRCode,
} from '../lib/offlineStore';
import {
  getConnectionStatus,
  onConnectionStatusChange,
  syncAll,
  requestBackgroundSync,
} from '../lib/syncManager';

export function useOfflineMode() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionStatus, setConnectionStatus] = useState<string>(getConnectionStatus());
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const unsubscribe = onConnectionStatusChange((status) => {
      setConnectionStatus(status);
      setIsOnline(status !== 'offline');
    });

    // Check pending count periodically
    const interval = setInterval(async () => {
      const count = await getOutboxCount();
      setPendingCount(count);
    }, 10000);

    // Initial count
    getOutboxCount().then(setPendingCount);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      unsubscribe();
      clearInterval(interval);
    };
  }, []);

  // Validate QR Code (online or offline)
  const validateQRCode = useCallback(async (code: string): Promise<{ valid: boolean; sector?: string; source: 'online' | 'offline' }> => {
    if (isOnline) {
      try {
        const response = await fetch(`/api/trpc/session.validate?input=${encodeURIComponent(JSON.stringify({ json: { code } }))}`, {
          credentials: 'include',
          signal: AbortSignal.timeout(5000),
        });
        if (response.ok) {
          const data = await response.json();
          const result = data?.result?.data?.json;
          return { valid: !!result?.valid, sector: result?.sector, source: 'online' };
        }
      } catch {
        // Fall through to offline validation
      }
    }
    // Offline validation
    const cached = await validateQRCodeOffline(code);
    return {
      valid: !!cached,
      sector: cached?.sector,
      source: 'offline',
    };
  }, [isOnline]);

  // Get available agents (online or offline)
  const getAvailableAgents = useCallback(async (): Promise<{ agents: OfflineAgent[]; source: 'online' | 'offline' }> => {
    if (isOnline) {
      try {
        const response = await fetch('/api/trpc/agent.list', {
          credentials: 'include',
          signal: AbortSignal.timeout(5000),
        });
        if (response.ok) {
          const data = await response.json();
          const agents = data?.result?.data?.json || [];
          return { agents: agents.filter((a: any) => a.status === 'available'), source: 'online' };
        }
      } catch {
        // Fall through to offline
      }
    }
    const cached = await getCachedAgents();
    return {
      agents: cached.filter((a) => a.status === 'available'),
      source: 'offline',
    };
  }, [isOnline]);

  // Create event (online or offline)
  const createEventOffline = useCallback(async (eventData: {
    eventType: string;
    classification: string;
    sector: string;
    personName: string;
    person2Name?: string;
    notes?: string;
  }): Promise<{ localId: number; source: 'offline' }> => {
    const idempotencyKey = generateIdempotencyKey();
    const localId = await saveOfflineEvent({
      ...eventData,
      status: 'detected',
      synced: false,
      createdAt: Date.now(),
      idempotencyKey,
    });

    // Also add to outbox for sync
    await addToOutbox({
      type: 'event',
      payload: JSON.stringify({ ...eventData, idempotencyKey }),
      synced: false,
      createdAt: Date.now(),
      attempts: 0,
      idempotencyKey,
    });

    // Request background sync if available
    await requestBackgroundSync('ella-sync-events');

    setPendingCount((prev) => prev + 1);
    return { localId, source: 'offline' };
  }, []);

  // Save image offline
  const saveImageOffline = useCallback(async (
    eventLocalId: number,
    blob: Blob,
    mimeType: string,
    type: 'photo' | 'video',
    origin: string,
  ): Promise<number> => {
    const localId = await saveOfflineImage({
      eventLocalId,
      blob,
      mimeType,
      type,
      origin,
      synced: false,
      createdAt: Date.now(),
    });

    await requestBackgroundSync('ella-sync-images');
    return localId;
  }, []);

  // Dispatch agent offline (queue for sync)
  const dispatchAgentOffline = useCallback(async (agentId: number, eventLocalId: number): Promise<void> => {
    const idempotencyKey = generateIdempotencyKey();
    await addToOutbox({
      type: 'dispatch',
      payload: JSON.stringify({ agentId, eventLocalId, idempotencyKey }),
      synced: false,
      createdAt: Date.now(),
      attempts: 0,
      idempotencyKey,
    });

    // Update local agent status
    const { updateCachedAgentStatus } = await import('../lib/offlineStore');
    await updateCachedAgentStatus(agentId, 'dispatched');

    await requestBackgroundSync('ella-sync-events');
    setPendingCount((prev) => prev + 1);
  }, []);

  // Manual sync trigger
  const triggerSync = useCallback(async () => {
    const result = await syncAll();
    const count = await getOutboxCount();
    setPendingCount(count);
    return result;
  }, []);

  return {
    isOnline,
    connectionStatus,
    pendingCount,
    validateQRCode,
    getAvailableAgents,
    createEventOffline,
    saveImageOffline,
    dispatchAgentOffline,
    triggerSync,
  };
}
